import { isFutureVisitAtIso } from "@/components/handshake/visitAt";
import {
  Permission,
  type BookingLifecycleStatus,
  type HandshakeSessionStatus,
} from "@/types";

export interface BookingLineItemChip {
  id: string;
  quantity: number;
  name: string;
  unitPriceCents?: number;
  totalPriceCents?: number;
  listUnitPriceCents?: number;
  listTotalPriceCents?: number;
  sellerDiscountCents?: number;
  originalUnitPriceCents?: number;
  originalTotalPriceCents?: number;
  /** Platform / Flitrip promo discount on this line. */
  flitripDiscountCents?: number;
}

/** Line fields required to aggregate a discount breakdown (no invented defaults). */
export interface BookingDiscountBreakdownLine {
  listTotalPriceCents: number;
  sellerDiscountCents: number;
  originalTotalPriceCents: number;
  flitripDiscountCents: number;
}

/** Aggregated price breakdown for partner/admin booking detail. */
export interface BookingDiscountBreakdown {
  listTotalCents: number;
  sellerDiscountCents: number;
  serviceTotalCents: number;
  flitripDiscountCents: number;
  hasSellerDiscount: boolean;
  hasFlitripDiscount: boolean;
  hasAnyDiscount: boolean;
}

function isCompleteDiscountLine(
  line: Partial<BookingDiscountBreakdownLine>,
): line is BookingDiscountBreakdownLine {
  return (
    typeof line.listTotalPriceCents === "number" &&
    typeof line.sellerDiscountCents === "number" &&
    typeof line.originalTotalPriceCents === "number" &&
    typeof line.flitripDiscountCents === "number"
  );
}

/**
 * Pre-discount total to strike through on a line, or null when none applies.
 * Prefers list when there is a seller markdown; otherwise catalog/original.
 */
export function lineItemStrikethroughTotalCents(line: {
  listTotalPriceCents?: number;
  sellerDiscountCents?: number;
  originalTotalPriceCents?: number;
  totalPriceCents: number;
}): number | null {
  const list = line.listTotalPriceCents;
  const original = line.originalTotalPriceCents;
  const seller = line.sellerDiscountCents ?? 0;
  const strike =
    seller > 0 && list != null ? list : (original ?? list ?? null);
  if (strike == null || strike <= line.totalPriceCents) {
    return null;
  }
  return strike;
}

/** Sum priced lines only; incomplete lines are skipped (fail closed). */
export function sumBookingDiscountBreakdown(
  lines: ReadonlyArray<Partial<BookingDiscountBreakdownLine>>,
): BookingDiscountBreakdown | null {
  if (lines.length === 0) {
    return null;
  }

  let listTotalCents = 0;
  let sellerDiscountCents = 0;
  let serviceTotalCents = 0;
  let flitripDiscountCents = 0;
  let sawPricedLine = false;

  for (const line of lines) {
    if (!isCompleteDiscountLine(line)) {
      continue;
    }
    sawPricedLine = true;
    listTotalCents += line.listTotalPriceCents;
    sellerDiscountCents += line.sellerDiscountCents;
    serviceTotalCents += line.originalTotalPriceCents;
    flitripDiscountCents += line.flitripDiscountCents;
  }

  if (!sawPricedLine) {
    return null;
  }

  const hasSellerDiscount = sellerDiscountCents > 0;
  const hasFlitripDiscount = flitripDiscountCents > 0;
  return {
    listTotalCents,
    sellerDiscountCents,
    serviceTotalCents,
    flitripDiscountCents,
    hasSellerDiscount,
    hasFlitripDiscount,
    hasAnyDiscount: hasSellerDiscount || hasFlitripDiscount,
  };
}

/**
 * Partner-facing display keys mapped from backend BookingStatus.
 * Terminal states use the same labels as the API enum; in-progress states stay simplified.
 */
export type PartnerDisplayStatus =
  | "created"
  | "pending"
  | "confirmed"
  | "completed"
  | "cancelled"
  | "abandoned"
  | "rejected";

const PARTNER_CONFIRMED_STATUSES: ReadonlySet<BookingLifecycleStatus> = new Set([
  "ACCEPTED",
]);

/** Upfront bookings that never completed card capture. */
const UPFRONT_UNPAID_STATUSES: ReadonlySet<BookingLifecycleStatus> = new Set([
  "CREATED",
  "ABANDONED",
]);

/** Simplified status labels shown to activity owners (sellers/hosts). */
export function isPartnerConfirmedBooking(
  status: BookingLifecycleStatus,
): boolean {
  return PARTNER_CONFIRMED_STATUSES.has(status);
}

/**
 * Partner/admin can cancel a confirmed booking before the visit instant passes.
 * Receipt-based: once a receipt total is set, cancel is closed (settlement in progress).
 */
export function showPartnerCancelBooking(booking: {
  status: BookingLifecycleStatus;
  visitAt: string;
  isReceiptBased?: boolean;
  receiptTotalCents?: number | null;
}): boolean {
  if (booking.status !== "ACCEPTED") {
    return false;
  }
  if (booking.isReceiptBased && booking.receiptTotalCents != null) {
    return false;
  }
  if (!booking.visitAt) {
    return true;
  }
  return isFutureVisitAtIso(booking.visitAt);
}

/** Receipt section on partner booking cards/detail (upload or already uploaded). */
export function showPartnerReceiptSection(booking: {
  isReceiptBased: boolean;
  receiptTotalCents?: number;
  status: BookingLifecycleStatus;
}): boolean {
  if (!booking.isReceiptBased) {
    return false;
  }
  if (booking.receiptTotalCents != null) {
    return true;
  }
  return isPartnerConfirmedBooking(booking.status);
}

export function showPartnerReceiptUpload(booking: {
  isReceiptBased: boolean;
  receiptTotalCents?: number;
  manualAmountRequired?: boolean;
  status: BookingLifecycleStatus;
}): boolean {
  return (
    booking.isReceiptBased &&
    booking.receiptTotalCents == null &&
    !booking.manualAmountRequired &&
    isPartnerConfirmedBooking(booking.status)
  );
}

/** Customer-paid total for upfront activities (checkout amount after promo/points). */
export function showUpfrontPaidAmount(booking: {
  isReceiptBased: boolean;
  amountPaidCents?: number;
  status: BookingLifecycleStatus;
}): boolean {
  return (
    !booking.isReceiptBased &&
    booking.amountPaidCents != null &&
    booking.amountPaidCents > 0 &&
    !UPFRONT_UNPAID_STATUSES.has(booking.status)
  );
}

export function getUpfrontPaidCents(booking: {
  isReceiptBased: boolean;
  amountPaidCents?: number;
  status: BookingLifecycleStatus;
}): number | null {
  return showUpfrontPaidAmount(booking) ? (booking.amountPaidCents ?? null) : null;
}

export function showPartnerManualReceiptAmount(booking: {
  isReceiptBased: boolean;
  receiptTotalCents?: number;
  manualAmountRequired?: boolean;
  status: BookingLifecycleStatus;
}): boolean {
  return (
    booking.isReceiptBased &&
    booking.receiptTotalCents == null &&
    booking.manualAmountRequired === true &&
    isPartnerConfirmedBooking(booking.status)
  );
}

/**
 * Map backend BookingStatus to partner badge styling.
 * If booking is still PENDING_HANDSHAKE but the linked session already closed,
 * show the closed outcome (legacy desync until data repair).
 * Handshake cancel/expire sync rejects the booking, so CANCELLED and EXPIRED
 * both map to "rejected" (not "cancelled").
 */
export function partnerDisplayStatus(
  status: BookingLifecycleStatus,
  handshakeStatus: HandshakeSessionStatus | null,
): PartnerDisplayStatus {
  if (status === "PENDING_HANDSHAKE") {
    if (handshakeStatus === "CANCELLED" || handshakeStatus === "EXPIRED") {
      return "rejected";
    }
    if (handshakeStatus === "CONFIRMED") {
      return "confirmed";
    }
    return "pending";
  }

  switch (status) {
    case "CREATED":
      return "created";
    case "ACCEPTED":
      return "confirmed";
    case "COMPLETED":
      return "completed";
    case "CANCELED":
      return "cancelled";
    case "ABANDONED":
      return "abandoned";
    case "REJECTED":
      return "rejected";
  }
}

/** Who is looking at a booking's handshake (customers never see it). */
export interface HandshakeViewer {
  customerMode: boolean;
  adminMode: boolean;
  hasPermission: (permission: Permission) => boolean;
}

export function canViewHandshake(viewer: HandshakeViewer): boolean {
  if (viewer.customerMode) {
    return false;
  }
  return viewer.adminMode
    ? viewer.hasPermission(Permission.HANDSHAKES_SESSIONS_READ_ANY)
    : viewer.hasPermission(Permission.HANDSHAKES_SESSIONS_READ_OWN);
}

/** Confirm/cancel a handshake (admins) or confirm/reply to it (partners). */
export function canInteractWithHandshake(viewer: HandshakeViewer): boolean {
  if (viewer.customerMode) {
    return false;
  }
  return viewer.adminMode
    ? viewer.hasPermission(Permission.HANDSHAKES_SESSION_CONFIRM_ANY) ||
        viewer.hasPermission(Permission.HANDSHAKES_SESSION_CANCEL)
    : viewer.hasPermission(Permission.HANDSHAKES_SESSION_CONFIRM) ||
        viewer.hasPermission(Permission.HANDSHAKES_MESSAGE_SEND);
}

/** Amount the admin booking detail header shows (label key under `detail.*`). */
export interface AdminHeaderAmount {
  kind: "receipt" | "depositPaid" | "amountPaid" | "total";
  cents: number | null;
}

/** Receipt total for receipt-based bookings, else what the customer paid, else the booking total. */
export function adminHeaderAmount(
  booking: {
    isReceiptBased: boolean;
    receiptTotalCents?: number;
    isDeposit?: boolean;
    totalPriceCents?: number;
  },
  customerPaidCents: number | null,
): AdminHeaderAmount {
  if (booking.isReceiptBased) {
    return { kind: "receipt", cents: booking.receiptTotalCents ?? null };
  }
  if (customerPaidCents != null) {
    return {
      kind: booking.isDeposit ? "depositPaid" : "amountPaid",
      cents: customerPaidCents,
    };
  }
  return { kind: "total", cents: booking.totalPriceCents ?? null };
}

export function formatCustomerDisplayName(firstName: string, lastName: string): string {
  return [firstName, lastName].filter(Boolean).join(" ").trim();
}
